import React, { memo, useState, useEffect } from 'react';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

// ============================================
// Types
// ============================================

interface OfflineBannerProps {
  className?: string;
}

// ============================================
// Icons
// ============================================

const WifiOffIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
    />
  </svg>
);

const WifiIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
    />
  </svg>
);

// ============================================
// Component
// ============================================

export const OfflineBanner: React.FC<OfflineBannerProps> = memo(({ className = '' }) => {
  const { isOnline, wasOffline } = useOnlineStatus();
  const [showReconnected, setShowReconnected] = useState(false);

  // Show "reconnected" message briefly when coming back online
  useEffect(() => {
    if (isOnline && wasOffline) {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  // Don't show anything if online and never was offline
  if (isOnline && !showReconnected) {
    return null;
  }

  return (
    <div
      className={`
        fixed bottom-4 left-1/2 -translate-x-1/2 z-50 
        px-4 py-2 rounded-2xl shadow-lg
        flex items-center gap-2
        animate-in slide-in-from-bottom-4
        ${isOnline 
          ? 'bg-emerald-500 text-white' 
          : 'bg-slate-800 text-white'
        }
        ${className}
      `}
      role="status"
      aria-live="polite"
    >
      {isOnline ? (
        <>
          <WifiIcon />
          <span className="text-sm font-bold">Back online</span>
        </>
      ) : (
        <>
          <WifiOffIcon />
          <span className="text-sm font-bold">You're offline</span>
          <span className="text-xs opacity-75 ml-1">Changes will sync when reconnected</span>
        </>
      )}
    </div>
  );
});

OfflineBanner.displayName = 'OfflineBanner';

export default OfflineBanner;
